import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShellLayout from '../components/AppShellLayout';
import { IconMail } from '../components/Icons';
import { capture } from '../analytics';

interface SubscriptionSender {
  senderEmail: string;
  senderName: string | null;
  emailCount: number;
  lastReceived: string | null;
  categorySource: string;
  category: string;
  labelled: boolean;
  listUnsubscribeUrl: string | null;
  unsubscribed: boolean;
  importanceScore: number | null;
  senderType: string | null;
  userImportanceOverride: string | null;
  inWindowEmailCount: number;
  totalEmailCount: number;
  unreadCount: number;
}

interface SuggestedAction {
  id: number;
  senderEmail: string;
  senderName: string | null;
  actionType: string;
  emailCount: number;
  createdAt: string;
}

interface SenderRule {
  ruleType: string;
  paramInt: number | null;
}

function ruleDescription(rule: SenderRule): string {
  if (rule.ruleType === 'keep_last_n') {
    return rule.paramInt === 1 ? 'Keep last 1 email' : `Keep last ${rule.paramInt} emails`;
  }
  if (rule.ruleType === 'archive_after_days') return `Archive after ${rule.paramInt} days`;
  if (rule.ruleType === 'always_skip_inbox') return 'Always skip inbox';
  if (rule.ruleType === 'always_keep_inbox') return 'Never auto-archive';
  return rule.ruleType;
}

interface SubscriptionsResponse {
  senders: SubscriptionSender[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

type RowAction = 'archiving' | 'trashing' | 'labelling' | 'unsubscribing';
type ActiveFilter = 'high-volume' | 'stale' | null;
type CategoryTab = 'subscription' | 'transactional' | 'notification' | 'marketing' | 'personal' | 'unknown' | 'all';

function SenderAvatar({ senderName, senderEmail }: { senderName: string | null; senderEmail: string }) {
  const [err, setErr] = useState(false);
  const domain = senderEmail.split('@')[1];
  const initial = ((senderName ?? senderEmail) || '?')[0].toUpperCase();

  if (!err && domain) {
    return (
      <img
        className="sender-avatar-img"
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
        alt=""
        width={28}
        height={28}
        onError={() => setErr(true)}
      />
    );
  }
  return <div className="sender-avatar-initials">{initial}</div>;
}

export default function SubscriptionsPage() {
  const [searchParams] = useSearchParams();
  const [senders, setSenders] = useState<SubscriptionSender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowActions, setRowActions] = useState<Record<string, RowAction>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(null);
  const [categoryTab, setCategoryTab] = useState<CategoryTab>(
    searchParams.get('filter') === 'important' ? 'all' : 'subscription'
  );
  const [totalSenders, setTotalSenders] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchMountedRef = React.useRef(false);
  const [confirmingArchive, setConfirmingArchive] = useState<string | null>(null);
  const [confirmingTrash, setConfirmingTrash] = useState<string | null>(null);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [toast, setToast] = useState<{
    message: string; actionId: number | null; undoable: boolean;
  } | null>(null);
  const [usage, setUsage] = useState<{ status: string; windowDays: number; isActive: boolean } | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<SuggestedAction[]>([]);
  const toastTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [senderRules, setSenderRules] = useState<Record<string, SenderRule>>({});
  const [openMenuEmail, setOpenMenuEmail] = useState<string | null>(null);
  const [daysInputEmail, setDaysInputEmail] = useState<string | null>(null);
  const [daysInputValue, setDaysInputValue] = useState('');

  useEffect(() => {
    fetchSubscriptions();
    fetchUsage();
    fetchSuggestedActions();
    fetchSenderRules();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openMenuEmail) return;
    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest('.rule-menu-wrapper')) {
        setOpenMenuEmail(null);
        setDaysInputEmail(null);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openMenuEmail]);

  // Debounce search: re-fetch from page 1 with the new query 300ms after the user stops typing.
  // Skip the initial mount (handled by the effect above).
  useEffect(() => {
    if (!searchMountedRef.current) { searchMountedRef.current = true; return; }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchSubscriptions(searchQuery.trim());
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchUsage() {
    try {
      const res = await fetch('/api/billing/usage');
      if (res.ok) setUsage(await res.json());
    } catch { /* non-critical */ }
  }

  async function fetchSenderRules() {
    try {
      const res = await fetch('/api/senders/rules');
      if (!res.ok) return;
      const list: Array<{ senderEmail: string; ruleType: string; paramInt: number | null }> = await res.json();
      const map: Record<string, SenderRule> = {};
      for (const item of list) map[item.senderEmail] = { ruleType: item.ruleType, paramInt: item.paramInt };
      setSenderRules(map);
    } catch { /* non-critical */ }
  }

  async function setSenderRuleForEmail(senderEmail: string, ruleType: string, paramInt: number | null) {
    const optimistic = { ruleType, paramInt };
    setSenderRules(prev => ({ ...prev, [senderEmail]: optimistic }));
    setOpenMenuEmail(null);
    setDaysInputEmail(null);
    try {
      await fetch(`/api/senders/${encodeURIComponent(senderEmail)}/rule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ruleType, paramInt }),
      });
    } catch {
      setSenderRules(prev => { const next = { ...prev }; delete next[senderEmail]; return next; });
    }
  }

  async function removeSenderRuleForEmail(senderEmail: string) {
    const prev = senderRules[senderEmail];
    setSenderRules(p => { const next = { ...p }; delete next[senderEmail]; return next; });
    setOpenMenuEmail(null);
    try {
      await fetch(`/api/senders/${encodeURIComponent(senderEmail)}/rule`, { method: 'DELETE' });
    } catch {
      if (prev) setSenderRules(p => ({ ...p, [senderEmail]: prev }));
    }
  }

  async function fetchSuggestedActions() {
    try {
      const res = await fetch('/api/suggested-actions');
      if (res.ok) setSuggestedActions(await res.json());
    } catch { /* non-critical */ }
  }

  async function toggleImportance(senderEmail: string) {
    const sender = senders.find(s => s.senderEmail === senderEmail);
    if (!sender) return;
    const nextOverride = isImportant(sender) ? 'low' : 'high';
    // Optimistic update
    setSenders(prev => prev.map(s =>
      s.senderEmail === senderEmail ? { ...s, userImportanceOverride: nextOverride } : s
    ));
    try {
      await fetch(`/api/senders/${encodeURIComponent(senderEmail)}/importance`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override: nextOverride }),
      });
    } catch {
      // Revert on failure
      setSenders(prev => prev.map(s =>
        s.senderEmail === senderEmail ? { ...s, userImportanceOverride: sender.userImportanceOverride } : s
      ));
    }
  }

  async function fetchSubscriptions(search = '') {
    setLoading(true);
    setError(null);
    try {
      const url = search
        ? `/api/subscriptions?page=1&pageSize=50&search=${encodeURIComponent(search)}`
        : '/api/subscriptions?page=1&pageSize=50';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: SubscriptionsResponse = await res.json();
      setSenders(data.senders);
      setTotalSenders(data.total);
      setHasMore(data.hasMore);
      setCurrentPage(1);
      // On initial (non-search) load, jump to first non-empty tab if subscription tab is empty
      if (!search) {
        setCategoryTab(prev => {
          if (data.senders.some(s => s.category === prev)) return prev;
          const order: CategoryTab[] = ['subscription', 'transactional', 'notification', 'marketing', 'personal', 'unknown'];
          return order.find(t => data.senders.some(s => s.category === t)) ?? 'all';
        });
      }
    } catch {
      setError('Failed to load subscriptions.');
    } finally {
      setLoading(false);
    }
  }

  async function loadMoreSenders() {
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const search = searchQuery.trim();
      const url = search
        ? `/api/subscriptions?page=${nextPage}&pageSize=50&search=${encodeURIComponent(search)}`
        : `/api/subscriptions?page=${nextPage}&pageSize=50`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: SubscriptionsResponse = await res.json();
      setSenders(prev => [...prev, ...data.senders]);
      setTotalSenders(data.total);
      setHasMore(data.hasMore);
      setCurrentPage(nextPage);
    } catch {
      setActionError('Failed to load more senders.');
    } finally {
      setLoadingMore(false);
    }
  }

  function setRowAction(senderEmail: string, action: RowAction | null) {
    setRowActions(prev => {
      const next = { ...prev };
      if (action == null) delete next[senderEmail];
      else next[senderEmail] = action;
      return next;
    });
  }

  function showToast(message: string, actionId: number | null, undoable: boolean) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, actionId, undoable });
    toastTimerRef.current = setTimeout(() => setToast(null), 10000);
  }

  async function undoAction(actionId: number) {
    setToast(null);
    try {
      const res = await fetch(`/api/actions/${actionId}/undo`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setActionError(data.error === 'reauth_required'
          ? 'Gmail reconnection required to undo.'
          : 'Undo failed. Please try again.');
        return;
      }
      await fetchSubscriptions();
    } catch {
      setActionError('Undo failed. Please try again.');
    }
  }

  async function archiveSender(senderEmail: string) {
    const sender = senders.find(s => s.senderEmail === senderEmail);
    setRowAction(senderEmail, 'archiving');
    setActionError(null);
    setReauthRequired(false);
    try {
      const res = await fetch('/api/subscriptions/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderEmail, senderName: sender?.senderName }),
      });
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'outside_trial_window') {
          setActionError('All emails from this sender are outside your free trial period. Upgrade to action them.');
        } else {
          setReauthRequired(true);
        }
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      capture('sender_archived', { email_count: data.inWindowCount ?? sender?.emailCount ?? 0 });
      setSenders(prev => prev.filter(s => s.senderEmail !== senderEmail));
      const toastMsg = data.suggestedActionSaved
        ? `Archived ${data.inWindowCount} emails from ${sender?.senderName || senderEmail}. ${data.outOfWindowCount} older emails saved as a suggested action.`
        : `Archived ${data.inWindowCount ?? sender?.emailCount ?? ''} emails from ${sender?.senderName || senderEmail}`;
      showToast(toastMsg, data.actionId ?? null, !!data.actionId);
    } catch {
      setActionError('Archive failed. Please try again.');
    } finally {
      setRowAction(senderEmail, null);
    }
  }

  async function unsubscribeSender(senderEmail: string) {
    const sender = senders.find(s => s.senderEmail === senderEmail);
    setRowAction(senderEmail, 'unsubscribing');
    setActionError(null);
    setReauthRequired(false);
    try {
      const res = await fetch('/api/subscriptions/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderEmail, senderName: sender?.senderName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setActionError(data.error ?? 'Unsubscribe failed. Please try again.');
        return;
      }
      setSenders(prev =>
        prev.map(s => s.senderEmail === senderEmail ? { ...s, unsubscribed: true } : s)
      );
      showToast(`Unsubscribed from ${sender?.senderName || senderEmail}`, null, false);
    } catch {
      setActionError('Unsubscribe failed. Please try again.');
    } finally {
      setRowAction(senderEmail, null);
    }
  }

  async function trashSender(senderEmail: string) {
    const sender = senders.find(s => s.senderEmail === senderEmail);
    setRowAction(senderEmail, 'trashing');
    setActionError(null);
    setReauthRequired(false);
    try {
      const res = await fetch('/api/subscriptions/trash', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderEmail, senderName: sender?.senderName }),
      });
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'outside_trial_window') {
          setActionError('All emails from this sender are outside your free trial period. Upgrade to action them.');
        } else {
          setReauthRequired(true);
        }
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSenders(prev => prev.filter(s => s.senderEmail !== senderEmail));
      const toastMsg = data.suggestedActionSaved
        ? `Deleted ${data.inWindowCount} emails from ${sender?.senderName || senderEmail}. ${data.outOfWindowCount} older emails saved as a suggested action.`
        : `Deleted ${data.inWindowCount ?? sender?.emailCount ?? ''} emails from ${sender?.senderName || senderEmail}`;
      showToast(toastMsg, data.actionId ?? null, !!data.actionId);
    } catch {
      setActionError('Delete failed. Please try again.');
    } finally {
      setRowAction(senderEmail, null);
    }
  }

  async function labelSender(senderEmail: string) {
    const sender = senders.find(s => s.senderEmail === senderEmail);
    setRowAction(senderEmail, 'labelling');
    setActionError(null);
    setReauthRequired(false);
    try {
      const res = await fetch('/api/subscriptions/label', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderEmail, senderName: sender?.senderName }),
      });
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'outside_trial_window') {
          setActionError('All emails from this sender are outside your free trial period. Upgrade to action them.');
        } else {
          setReauthRequired(true);
        }
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      capture('sender_labelled');
      setSenders(prev => prev.map(s =>
        s.senderEmail === senderEmail ? { ...s, labelled: true } : s
      ));
      const toastMsg = data.suggestedActionSaved
        ? `Labelled ${data.inWindowCount} emails from ${sender?.senderName || senderEmail}. ${data.outOfWindowCount} older emails saved as a suggested action.`
        : `Labelled emails from ${sender?.senderName || senderEmail}`;
      showToast(toastMsg, data.actionId ?? null, !!data.actionId);
    } catch {
      setActionError('Label failed. Please try again.');
    } finally {
      setRowAction(senderEmail, null);
    }
  }

  async function bulkArchive() {
    const targets = [...displayedSenders];
    setBulkConfirming(false);
    setBulkProgress({ done: 0, total: targets.length });
    setActionError(null);
    setReauthRequired(false);

    for (let i = 0; i < targets.length; i++) {
      try {
        const res = await fetch('/api/subscriptions/archive', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ senderEmail: targets[i].senderEmail }),
        });
        if (res.status === 403) {
          const data = await res.json().catch(() => ({}));
          if (data.error === 'outside_trial_window') {
            // skip this sender silently in bulk mode
          } else {
            setReauthRequired(true);
            break;
          }
        } else if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        } else {
          setSenders(prev => prev.filter(s => s.senderEmail !== targets[i].senderEmail));
        }
      } catch {
        setActionError(`Failed to archive ${targets[i].senderName || targets[i].senderEmail}. Stopped.`);
        break;
      }
      setBulkProgress({ done: i + 1, total: targets.length });
    }

    setBulkProgress(null);
    capture('bulk_archive_completed', { sender_count: targets.length });
  }

  async function acceptSuggestedAction(id: number, senderEmail: string, senderName: string | null, actionType: string) {
    try {
      const res = await fetch(`/api/suggested-actions/${id}/accept`, { method: 'POST' });
      if (res.status === 403) { setReauthRequired(true); return; }
      if (!res.ok) throw new Error();
      setSuggestedActions(prev => prev.filter(a => a.id !== id));
      setSenders(prev => prev.filter(s => s.senderEmail !== senderEmail));
      showToast(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)}d emails from ${senderName || senderEmail}`, null, false);
    } catch {
      setActionError('Failed to accept suggested action. Please try again.');
    }
  }

  async function dismissSuggestedAction(id: number) {
    try {
      await fetch(`/api/suggested-actions/${id}/dismiss`, { method: 'POST' });
      setSuggestedActions(prev => prev.filter(a => a.id !== id));
    } catch { /* non-critical */ }
  }

  async function acceptAllSuggestedActions() {
    for (const action of [...suggestedActions]) {
      await acceptSuggestedAction(action.id, action.senderEmail, action.senderName, action.actionType);
    }
  }

  function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  }

  // Derived metrics
  const totalEmails = senders.reduce((sum, s) => sum + s.emailCount, 0);
  const topSender = senders.reduce<SubscriptionSender | null>(
    (top, s) => (!top || s.emailCount > top.emailCount) ? s : top,
    null
  );
  const highVolumeSenders = senders.filter(s => s.emailCount > 20);
  const staleSenders = senders.filter(s => {
    if (!s.lastReceived) return false;
    return (Date.now() - new Date(s.lastReceived).getTime()) / 86_400_000 > 30;
  });
  const cleanupOpportunities = new Set([
    ...highVolumeSenders.map(s => s.senderEmail),
    ...staleSenders.map(s => s.senderEmail),
  ]).size;

  function isImportant(s: SubscriptionSender) {
    return s.userImportanceOverride === 'high' || (s.importanceScore != null && s.importanceScore >= 7 && !s.userImportanceOverride);
  }

  const importantFirst = searchParams.get('filter') === 'important';

  const filteredByCategory = categoryTab === 'all'
    ? senders
    : senders.filter(s => s.category === categoryTab);

  const filteredByTag = activeFilter === 'high-volume'
    ? filteredByCategory.filter(s => s.emailCount > 20)
    : activeFilter === 'stale'
      ? filteredByCategory.filter(s => !!s.lastReceived && (Date.now() - new Date(s.lastReceived).getTime()) / 86_400_000 > 30)
      : filteredByCategory;

  const isTrialRestricted = usage && !usage.isActive;
  const displayedSenders = importantFirst
    ? [...filteredByTag].sort((a, b) => (b.importanceScore ?? 5) - (a.importanceScore ?? 5))
    : isTrialRestricted
      ? [...filteredByTag].sort((a, b) => {
          const aActionable = a.inWindowEmailCount > 0 ? 1 : 0;
          const bActionable = b.inWindowEmailCount > 0 ? 1 : 0;
          if (bActionable !== aActionable) return bActionable - aActionable;
          return b.unreadCount !== a.unreadCount ? b.unreadCount - a.unreadCount : b.emailCount - a.emailCount;
        })
      : [...filteredByTag].sort((a, b) =>
          b.unreadCount !== a.unreadCount ? b.unreadCount - a.unreadCount : b.emailCount - a.emailCount
        );

  const categoryTabCounts: Record<string, number> = {
    all: senders.length,
    subscription: senders.filter(s => s.category === 'subscription').length,
    transactional: senders.filter(s => s.category === 'transactional').length,
    notification: senders.filter(s => s.category === 'notification').length,
    marketing: senders.filter(s => s.category === 'marketing').length,
    personal: senders.filter(s => s.category === 'personal').length,
    unknown: senders.filter(s => s.category === 'unknown').length,
  };

  const { t } = useTranslation();

  const CATEGORY_TABS: { key: CategoryTab; labelKey: string }[] = [
    { key: 'subscription', labelKey: 'subscriptions.categoryTabs.subscription' },
    { key: 'transactional', labelKey: 'subscriptions.categoryTabs.transactional' },
    { key: 'notification', labelKey: 'subscriptions.categoryTabs.notification' },
    { key: 'marketing', labelKey: 'subscriptions.categoryTabs.marketing' },
    { key: 'personal', labelKey: 'subscriptions.categoryTabs.personal' },
    { key: 'unknown', labelKey: 'subscriptions.categoryTabs.unknown' },
    { key: 'all', labelKey: 'subscriptions.categoryTabs.all' },
  ];

  const isBusy = bulkProgress != null || Object.keys(rowActions).length > 0;

  return (
    <AppShellLayout>
      <div className="page-header">
        <h1>{t('subscriptions.title')}</h1>
        <p>{t('subscriptions.subtitle')}</p>
      </div>

      {reauthRequired && (
        <div className="inline-banner inline-banner--warn">
          {t('subscriptions.reconnectBanner')}{' '}
          <Link to="/app/connect">{t('subscriptions.reconnectLink')}</Link>
        </div>
      )}

      {actionError && (
        <div className="inline-banner inline-banner--error">
          {actionError}
        </div>
      )}

      {/* Metric cards */}
      {loading ? (
        <div className="dashboard-grid subs-section">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="dashboard-card">
              <div className="skeleton skeleton-label" />
              <div className="skeleton skeleton-value" />
            </div>
          ))}
        </div>
      ) : !error && (
        <div className="dashboard-grid subs-section">
          <div className="dashboard-card">
            <div className="card-label">{t('subscriptions.sendersDetected')}</div>
            <div className="card-value">{totalSenders.toLocaleString()}</div>
          </div>
          <div className="dashboard-card">
            <div className="card-label">{t('subscriptions.totalEmails')}</div>
            <div className="card-value">{totalEmails.toLocaleString()}</div>
          </div>
          <div className="dashboard-card">
            <div className="card-label">{t('subscriptions.topSender')}</div>
            <div className="card-value card-value--name">
              {topSender ? (topSender.senderName || topSender.senderEmail.split('@')[0]) : '—'}
            </div>
            {topSender && (
              <div className="card-sub">{topSender.emailCount.toLocaleString()} {t('subscriptions.emails')}</div>
            )}
          </div>
          <div className="dashboard-card">
            <div className="card-label">{t('subscriptions.cleanupOpportunities')}</div>
            <div className="card-value">{cleanupOpportunities.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Quick action suggestions */}
      {!loading && !error && (highVolumeSenders.length > 0 || staleSenders.length > 0) && (
        <div className="quick-actions subs-section">
          <div className="quick-actions-title">{t('subscriptions.suggestions')}</div>
          <div className="quick-action-items">
            {highVolumeSenders.length > 0 && (
              <div className={`quick-action-item${activeFilter === 'high-volume' ? ' quick-action-item--active' : ''}`}>
                <span>
                  <strong>{highVolumeSenders.length}</strong>{' '}
                  sender{highVolumeSenders.length !== 1 ? 's have' : ' has'} sent more than 20 emails
                </span>
                <button
                  className="btn-secondary"
                  onClick={() => setActiveFilter(f => f === 'high-volume' ? null : 'high-volume')}
                >
                  {activeFilter === 'high-volume' ? 'Show all' : 'Review'}
                </button>
              </div>
            )}
            {staleSenders.length > 0 && (
              <div className={`quick-action-item${activeFilter === 'stale' ? ' quick-action-item--active' : ''}`}>
                <span>
                  <strong>{staleSenders.length}</strong>{' '}
                  subscription{staleSenders.length !== 1 ? 's haven\'t' : ' hasn\'t'} emailed in 30+ days
                </span>
                <button
                  className="btn-secondary"
                  onClick={() => setActiveFilter(f => f === 'stale' ? null : 'stale')}
                >
                  {activeFilter === 'stale' ? 'Show all' : 'Review'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Skeleton table */}
      {loading && (
        <div className="table-scroll-wrapper">
          <table className="senders-table">
            <thead>
              <tr>
                <th>{t('subscriptions.sender')}</th>
                <th>{t('subscriptions.emails')}</th>
                <th>{t('subscriptions.lastReceived')}</th>
                <th>{t('subscriptions.category')}</th>
                <th>{t('subscriptions.actions_col')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i}>
                  <td>
                    <div className="sender-cell">
                      <div className="skeleton sender-avatar-skeleton" />
                      <div>
                        <div className="skeleton skeleton-text" />
                        <div className="skeleton skeleton-text-sm mt-4" />
                      </div>
                    </div>
                  </td>
                  <td><div className="skeleton skeleton-badge" /></td>
                  <td><div className="skeleton skeleton-text-sm" /></td>
                  <td><div className="skeleton skeleton-badge" /></td>
                  <td><div className="skeleton skeleton-actions" /></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <p className="state-message state-error">
          {error}{' '}
          <button className="link-btn" onClick={() => fetchSubscriptions()}>Retry</button>
        </p>
      )}

      {!loading && !error && senders.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon"><IconMail size={22} /></div>
          <p>No subscriptions found yet.</p>
          <p>
            <Link to="/app/connect">Connect Gmail</Link> then run a scan from the{' '}
            <Link to="/app/dashboard">Dashboard</Link>.
          </p>
        </div>
      )}

      {!loading && !error && senders.length > 0 && (
        <>
          {/* Trial window banner */}
          {usage && !usage.isActive && senders.some(s => s.totalEmailCount > s.inWindowEmailCount) && (
            <div className="inline-banner inline-banner--warn trial-window-banner subs-section">
              Some emails are outside your {usage.windowDays}-day free trial preview.{' '}
              <Link to="/pricing" onClick={() => capture('upgrade_prompt_clicked', { source: 'trial_banner' })}>Upgrade to action your full inbox →</Link>
            </div>
          )}

          {/* Suggested actions panel */}
          {usage?.isActive && suggestedActions.length > 0 && (
            <div className="inline-banner inline-banner--info suggested-actions-panel subs-section">
              <strong>You have {suggestedActions.length} suggested action{suggestedActions.length !== 1 ? 's' : ''} saved from your trial.</strong>
              {suggestedActions.map(a => (
                <div key={a.id} className="suggested-action-row">
                  <span>{a.senderName || a.senderEmail} — {a.actionType} {a.emailCount} emails</span>
                  <button className="btn-secondary" onClick={() => acceptSuggestedAction(a.id, a.senderEmail, a.senderName, a.actionType)}>Accept</button>
                  <button className="btn-ghost" onClick={() => dismissSuggestedAction(a.id)}>Dismiss</button>
                </div>
              ))}
              {suggestedActions.length > 1 && (
                <button className="btn-secondary" onClick={acceptAllSuggestedActions}>Accept all</button>
              )}
            </div>
          )}

          {/* Category filter tabs */}
          <div className="category-tabs subs-section">
            {CATEGORY_TABS.filter(tab => categoryTabCounts[tab.key] > 0 || tab.key === 'all').map(tab => (
              <button
                key={tab.key}
                className={`category-tab${categoryTab === tab.key ? ' category-tab--active' : ''}`}
                onClick={() => { setCategoryTab(tab.key); setActiveFilter(null); }}
              >
                {t(tab.labelKey)}
                <span className="category-tab-count">{categoryTabCounts[tab.key]}</span>
              </button>
            ))}
          </div>

          {/* Table toolbar: search + bulk archive */}
          <div className="table-toolbar subs-section">
            <input
              className="search-input"
              type="search"
              placeholder={t('subscriptions.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              disabled={isBusy}
            />
            {displayedSenders.length > 0 && !bulkConfirming && !isBusy && (
              <button className="btn-secondary" onClick={() => setBulkConfirming(true)}>
                {t('subscriptions.archiveAllVisible', { count: displayedSenders.length })}
              </button>
            )}
            {bulkConfirming && (
              <div className="bulk-confirm">
                <span className="bulk-confirm-text">{t('subscriptions.archiveAllConfirm', { count: displayedSenders.length })}</span>
                <button className="btn-secondary btn-secondary--danger" onClick={bulkArchive}>{t('common.confirm')}</button>
                <button className="btn-ghost" onClick={() => setBulkConfirming(false)}>{t('common.cancel')}</button>
              </div>
            )}
            {isBusy && bulkProgress && (
              <span className="bulk-progress">
                {t('subscriptions.bulkArchiving', { done: bulkProgress.done, total: bulkProgress.total })}
              </span>
            )}
          </div>

          {(activeFilter || searchQuery.trim()) && (
            <div className="filter-banner">
              {t('subscriptions.showingFiltered', { shown: displayedSenders.length, total: senders.length })}
              {activeFilter && (
                <> — <button className="link-btn" onClick={() => setActiveFilter(null)}>{t('subscriptions.clearFilter')}</button></>
              )}
              {searchQuery.trim() && (
                <> — <button className="link-btn" onClick={() => setSearchQuery('')}>{t('subscriptions.clearSearch')}</button></>
              )}
            </div>
          )}

          <div className="table-scroll-wrapper">
            <table className="senders-table">
              <thead>
                <tr>
                  <th>Sender</th>
                  <th>Emails</th>
                  <th>Last received</th>
                  <th>Category</th>
                  <th>Actions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {displayedSenders.map(s => {
                  const busy = rowActions[s.senderEmail];
                  const confirming = confirmingArchive === s.senderEmail;
                  const confirmingDelete = confirmingTrash === s.senderEmail;
                  const fullyLocked = !!usage && !usage.isActive && s.inWindowEmailCount === 0;
                  return (
                    <tr key={s.senderEmail}>
                      <td>
                        <div className="sender-cell">
                          <SenderAvatar senderName={s.senderName} senderEmail={s.senderEmail} />
                          <div>
                            <div className="sender-name">
                              {s.senderName || s.senderEmail}
                              <button
                                className={`importance-star${isImportant(s) ? ' importance-star--active' : ''}`}
                                title={isImportant(s) ? 'Mark as not important' : 'Mark as important'}
                                onClick={() => toggleImportance(s.senderEmail)}
                                aria-label={isImportant(s) ? 'Marked important' : 'Mark as important'}
                              >
                                {isImportant(s) ? '★' : '☆'}
                              </button>
                            </div>
                            {s.senderName && <div className="sender-email">{s.senderEmail}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="email-count-badge">{s.emailCount}</span>
                        {usage && !usage.isActive && s.totalEmailCount > s.inWindowEmailCount && (
                          <span
                            className="trial-lock-badge"
                            title={`${s.totalEmailCount - s.inWindowEmailCount} emails are outside your ${usage.windowDays}-day free trial period. Upgrade to action these.`}
                          >
                            {s.totalEmailCount - s.inWindowEmailCount} locked
                          </span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>{formatDate(s.lastReceived)}</td>
                      <td>
                        <span className={`category-badge category-badge--${s.category}`}>
                          {s.category.charAt(0).toUpperCase() + s.category.slice(1)}
                        </span>
                      </td>
                      <td>
                        {fullyLocked ? (
                          <div className="row-actions">
                            <span className="fully-locked-msg" title="All emails from this sender are outside your free trial period.">
                              🔒 <Link to="/pricing" onClick={() => capture('upgrade_prompt_clicked', { source: 'action_blocked' })}>Upgrade</Link>
                            </span>
                          </div>
                        ) : confirming ? (
                          <div className="row-actions row-actions--confirm">
                            <span className="archive-confirm-text">{t('subscriptions.archiveCountConfirm', { count: s.inWindowEmailCount || s.emailCount })}</span>
                            <button
                              className="btn-secondary btn-secondary--danger"
                              onClick={() => { setConfirmingArchive(null); archiveSender(s.senderEmail); }}
                            >
                              {t('common.confirm')}
                            </button>
                            <button className="btn-ghost" onClick={() => setConfirmingArchive(null)}>
                              {t('common.cancel')}
                            </button>
                          </div>
                        ) : confirmingDelete ? (
                          <div className="row-actions row-actions--confirm">
                            <span className="archive-confirm-text">{t('subscriptions.trashCountConfirm', { count: s.inWindowEmailCount || s.emailCount })}</span>
                            <button
                              className="btn-secondary btn-secondary--danger"
                              onClick={() => { setConfirmingTrash(null); trashSender(s.senderEmail); }}
                            >
                              {t('common.confirm')}
                            </button>
                            <button className="btn-ghost" onClick={() => setConfirmingTrash(null)}>
                              {t('common.cancel')}
                            </button>
                          </div>
                        ) : (
                          <div className="row-actions">
                            <button
                              className="btn-secondary"
                              disabled={busy != null || isBusy || fullyLocked}
                              onClick={() => setConfirmingArchive(s.senderEmail)}
                            >
                              {busy === 'archiving' ? t('subscriptions.actions.archiving') : t('subscriptions.actions.archive')}
                            </button>
                            <button
                              className="btn-secondary btn-secondary--danger"
                              disabled={busy != null || isBusy || fullyLocked}
                              onClick={() => setConfirmingTrash(s.senderEmail)}
                            >
                              {busy === 'trashing' ? t('subscriptions.actions.trashing') : t('subscriptions.actions.trash')}
                            </button>
                            {s.listUnsubscribeUrl != null && (
                              <button
                                className="btn-secondary"
                                disabled={busy != null || s.unsubscribed || isBusy || fullyLocked}
                                onClick={() => unsubscribeSender(s.senderEmail)}
                              >
                                {busy === 'unsubscribing' ? t('subscriptions.actions.unsubscribing') : s.unsubscribed ? t('subscriptions.actions.unsubscribed') : t('subscriptions.actions.unsubscribe')}
                              </button>
                            )}
                            <button
                              className="btn-secondary"
                              disabled={busy != null || s.labelled || isBusy || fullyLocked}
                              onClick={() => labelSender(s.senderEmail)}
                            >
                              {busy === 'labelling' ? t('subscriptions.actions.labelling') : s.labelled ? t('subscriptions.actions.labelled') : t('subscriptions.actions.label')}
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="rule-menu-wrapper">
                          {senderRules[s.senderEmail] && (
                            <span className="rule-active-badge" title={ruleDescription(senderRules[s.senderEmail])}>
                              {ruleDescription(senderRules[s.senderEmail])}
                            </span>
                          )}
                          <button
                            className="btn-ghost rule-menu-btn"
                            title="Automation rule"
                            onClick={() => {
                              setOpenMenuEmail(prev => prev === s.senderEmail ? null : s.senderEmail);
                              setDaysInputEmail(null);
                              setDaysInputValue('');
                            }}
                          >
                            ⋮
                          </button>
                          {openMenuEmail === s.senderEmail && (
                            <div className="rule-menu-dropdown">
                              {senderRules[s.senderEmail] ? (
                                <>
                                  <div className="rule-menu-current">
                                    Active: {ruleDescription(senderRules[s.senderEmail])}
                                  </div>
                                  <button className="rule-menu-item rule-menu-item--danger" onClick={() => removeSenderRuleForEmail(s.senderEmail)}>
                                    Remove rule
                                  </button>
                                </>
                              ) : (
                                <>
                                  {daysInputEmail === s.senderEmail ? (
                                    <div className="rule-menu-days-input">
                                      <input
                                        className="days-input"
                                        type="number"
                                        min={1}
                                        placeholder="Days"
                                        value={daysInputValue}
                                        onChange={e => setDaysInputValue(e.target.value)}
                                        autoFocus
                                      />
                                      <button
                                        className="btn-secondary"
                                        disabled={!daysInputValue || parseInt(daysInputValue, 10) <= 0}
                                        onClick={() => setSenderRuleForEmail(s.senderEmail, 'archive_after_days', parseInt(daysInputValue, 10))}
                                      >
                                        Save
                                      </button>
                                      <button className="btn-ghost" onClick={() => setDaysInputEmail(null)}>✕</button>
                                    </div>
                                  ) : (
                                    <button className="rule-menu-item" onClick={() => { setDaysInputEmail(s.senderEmail); setDaysInputValue(''); }}>
                                      Archive after X days…
                                    </button>
                                  )}
                                  <button className="rule-menu-item" onClick={() => setSenderRuleForEmail(s.senderEmail, 'keep_last_n', 1)}>
                                    Keep last email only
                                  </button>
                                  <button className="rule-menu-item" onClick={() => setSenderRuleForEmail(s.senderEmail, 'always_skip_inbox', null)}>
                                    Always skip inbox
                                  </button>
                                  <button className="rule-menu-item" onClick={() => setSenderRuleForEmail(s.senderEmail, 'always_keep_inbox', null)}>
                                    Never auto-archive
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {displayedSenders.length === 0 && (
            <p className="state-message">{t('subscriptions.emptySearch', { query: searchQuery })}</p>
          )}

          {hasMore && !searchQuery.trim() && !activeFilter && (
            <div className="load-more-wrapper">
              <button
                className="btn-secondary"
                onClick={loadMoreSenders}
                disabled={loadingMore || isBusy}
              >
                {loadingMore ? t('subscriptions.loading2') : t('subscriptions.loadMore', { loaded: senders.length, total: totalSenders })}
              </button>
            </div>
          )}
        </>
      )}
      {toast && (
        <div className="action-toast">
          <span className="action-toast-message">{toast.message}</span>
          <div className="action-toast-actions">
            {toast.undoable && toast.actionId && (
              <button className="action-toast-undo" onClick={() => undoAction(toast.actionId!)}>
                {t('subscriptions.undo')}
              </button>
            )}
            <button className="action-toast-dismiss" onClick={() => setToast(null)} aria-label="Dismiss">
              ×
            </button>
          </div>
        </div>
      )}
    </AppShellLayout>
  );
}
